// ----------------------------------------------
// Analytics
// ----------------------------------------------
export namespace Analytics {
  export enum EventName {
    ChangedPromptExampleCategory = 'Octopus - Change example prompt category',
    ChangedTitle = 'Octopus - Title changed',
    CompletedReasoning = 'Octopus - Reasoning',
    CopiedChatId = 'Octopus - Chat ID copied',
    DeselectedAudience = 'Octopus - Remove an audience',
    ExportedInsightAsImage = 'Octopus - Click export as PNG',
    InitiatedDatasetChange = 'Octopus - Dataset intent',
    OpenedApp = 'Octopus - App opened',
    OpenedAudienceMenu = 'Octopus - Click add audience',
    OpenedRecentChat = 'Octopus - Chat opened',
    OpenedSources = 'Octopus - Click sources',
    SearchAudience = 'Octopus - Type an audience',
    SelectedAudience = 'Octopus - Select an audience',
    SelectedChartMetric = 'Octopus - Select chart metric',
    SelectedDataset = 'Octopus - Dataset selected',
    SelectedPromptExample = 'Octopus - Homepage prompt clicked',
    SentMessage = 'Octopus - Query submitted',
    ViewedReasoningOverview = 'Octopus - Click thinking overview',
    ViewedSourceInfoDialog = 'Octopus - Click find out more about the data',
    ViewedMoreThinkingOverviewItems = 'Octopus - Click view more',
    ViewedSource = 'Octopus - Source viewed',
    ReportedPositiveFeedback = 'Octopus - Positive feedback reported',
    SelectedNegativeFeedback = 'Octopus - Negative feedback clicked',
    ReportedNegativeFeedback = 'Octopus - Negative feedback reported',
    ReportedNegativeFeedbackDetails = 'Octopus - Negative feedback - Details',
    UndidNegativeFeedback = 'Octopus - Negative feedback removed',
    ViewedMoreNegativeFeedbackReasons = 'Octopus - Negative feedback - More clicked'
  }

  export namespace EventProps {
    export interface CompletedReasoning extends ChatProps {
      nb_duration_secs: number;
      nb_steps: number;
    }

    interface ChatProps {
      chat_id?: string;
    }

    export interface ChangedTitle extends ChatProps {
      previous_title: string;
      new_title: string;
    }

    export interface CopiedChatId extends ChatProps {}

    export interface DatasetTransitionProps {
      dataset_before: string;
      dataset_after: string;
    }

    export interface DatasetProps {
      dataset_details: string;
    }

    export interface DeselectedAudience extends ChatProps, DatasetProps {
      audience_id: string;
      audience_name: string;
      audience_type: string;
    }

    export interface ExportedInsightAsImage extends ChatProps, DatasetProps {
      attributes_codes: string[];
      audience_ids: string[];
      audience_names: string[];
      chart_metric: string;
      locations: string[];
      namespaces: string[];
      question_codes: string[];
      question_names: string[];
      waves: string[];
    }

    export interface InitiatedDatasetChange
      extends ChatProps,
        DatasetTransitionProps {}

    export interface OpenedAudienceMenu extends ChatProps, DatasetProps {}

    export interface OpenedRecentChat extends ChatProps {
      place: 'side_panel';
    }

    export interface OpenedSources extends ChatProps {
      nb_sources: number;
    }

    export interface SearchedAudience extends ChatProps, DatasetProps {}

    export interface SelectedAudience extends ChatProps, DatasetProps {
      audience_id: string;
      audience_name: string;
      audience_type: string;
    }

    export interface SelectedChartMetric extends ChatProps {
      chart_metric: string;
      query: string;
    }

    export interface SelectedDataset
      extends ChatProps,
        DatasetTransitionProps {}

    export interface SelectedPromptExample extends DatasetProps {
      prompt_category: string;
      prompt_template: string;
    }

    export interface SentMessage extends ChatProps, DatasetProps {
      method: 'click' | 'enter' | 'template_query' | 'home' | 'retry';
      nb_audiences: number;
      nb_ai_audiences: number;
      nb_gwi_audience: number;
      nb_my_audience: number;
      nb_shared_audience: number;
      nb_sources: number;
      nb_topics: number;
      nb_total_added_audience: number;
      query: string;
      query_ranking: number;
      origin: 'homepage' | 'octopus';
    }

    export interface ViewedReasoningOverview extends ChatProps {}

    export interface ViewedSource extends ChatProps, DatasetProps {
      audience_ids: string[];
      audience_names: string[];
      attributes_codes: string[];
      chart_metric: string;
      insight_id: string;
      locations: string[];
      namespaces: string[];
      nb_audiences: number;
      query: string;
      question_codes: string[];
      question_names: string[];
      source_text: string;
      waves: string[];
    }

    export interface ReportedPositiveFeedback extends ChatProps {
      query: string;
    }

    export interface SelectedNegativeFeedback extends ChatProps {
      query: string;
    }

    export interface ReportedNegativeFeedback extends ChatProps {
      query: string;
      origin: 'chat' | 'popup';
      reason_category: string;
    }

    export interface ReportedNegativeFeedbackDetails extends ChatProps {
      query: string;
      additional_feedback: string;
    }

    export interface UndidNegativeFeedback extends ChatProps {
      query: string;
      origin: 'chat' | 'popup';
      reason_category: string;
    }

    export interface ViewedMoreNegativeFeedbackReasons extends ChatProps {
      query: string;
    }

    export type AnyCompatible =
      | EventProps.ChangedTitle
      | EventProps.CompletedReasoning
      | EventProps.CopiedChatId
      | EventProps.DatasetProps
      | EventProps.DeselectedAudience
      | EventProps.ExportedInsightAsImage
      | EventProps.InitiatedDatasetChange
      | EventProps.OpenedAudienceMenu
      | EventProps.OpenedRecentChat
      | EventProps.OpenedSources
      | EventProps.ReportedPositiveFeedback
      | EventProps.ReportedNegativeFeedback
      | EventProps.ReportedNegativeFeedbackDetails
      | EventProps.SearchedAudience
      | EventProps.SelectedAudience
      | EventProps.SelectedChartMetric
      | EventProps.SelectedDataset
      | EventProps.SelectedNegativeFeedback
      | EventProps.SelectedPromptExample
      | EventProps.SentMessage
      | EventProps.UndidNegativeFeedback
      | EventProps.ViewedSource
      | EventProps.ViewedReasoningOverview
      | EventProps.ViewedMoreNegativeFeedbackReasons;
  }
}
